import { useEffect, useState } from 'react';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';
import { deleteLayerByCustomId } from '@utils/map/helpers';

/**
 * Creates and registers a WebGL vector layer on the map for the lifetime of the
 * calling component, then removes it on unmount.
 *
 * @param {Object} mapObject - OpenLayers map instance
 * @param {string} layerId - Unique customLayerId for the layer
 * @param {Object} [options] - Layer configuration
 * @param {string} [options.name='Layer'] - Display name for the layer
 * @param {number} [options.zIndex=10] - Stacking order
 * @param {boolean} [options.visible=false] - Initial visibility
 * @param {Object} [options.style={}] - WebGL flat style object
 * @returns {import('ol/layer/WebGLVector').default|null} The layer instance (null until created)
 */
export const useWebGLVectorLayer = (
	mapObject,
	layerId,
	{ name = 'Layer', zIndex = 10, visible = false, style = {} } = {}
) => {
	const [layer, setLayer] = useState(null);

	useEffect(() => {
		if (!mapObject) return;

		const layerInstance = new WebGLVectorLayer({
			name,
			zIndex,
			visible,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
			style,
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);

		return () => {
			deleteLayerByCustomId(mapObject, layerId);
			setLayer(null);
		};
	}, []);

	// Keep the layer's stacking order in sync when zIndex changes after creation
	useEffect(() => {
		layer?.setZIndex(zIndex);
	}, [layer, zIndex]);

	return layer;
};
